/* eslint-disable @typescript-eslint/naming-convention */
import { FC, ReactNode, useCallback } from 'react';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { CalculatorStore, CalculatorType } from '../stores/calculator.store';
import { ChatLayout } from '../../common/components/chat-layout/chat-layout';
import { Breadcrumbs } from '../../common/components/breadcrumbs/breadcrumbs';
import { MessageRow } from '../../common/components/message-row/message-row';
import { Loader } from '../../common/components/loader/loader';
import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { MessageType } from '../../common/models/message-type.model';
import { ButtonToggle, Flex, Text } from '@servicetitan/anvil2';
import { CalculatorApi } from '../../common/api/calculator.api';
import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';
import { CalculateSubCoolAnswer } from './answers/calculate-sub-cool.answer';
import { CalculateSuperHeatAnswer } from './answers/calculate-super-heat.answer';
import { TargetSuperHeatAnswer } from './answers/target-super-heat.answer';
import { CalculatorDuctSizingAnswer } from './answers/calculator-duct-sizing.answer';
import { NitrogenPressureTestAnswer } from './answers/nitrogen-pressure-test.answer';
import { PressureSetPointsAnswer } from './answers/pressure-set-points.answer';
import { TxvSizeCalculatorAnswer } from './answers/txv-size-calculator.answer';
import { AirflowDiagnosticsAnswer } from './answers/airflow-diagnostics.answer';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { AirflowDiagnosticsForm } from './forms/airflow-diagnostics.form';
import { CalculatorDuctSizingForm } from './forms/calculator-duct-sizing.form';
import { NitrogenPressureTestForm } from './forms/nitrogen-pressure-test.form';
import { PressureSetPointsForm } from './forms/pressure-set-points.form';
import { TxvSizeCalculatorForm } from './forms/txv-size-calculator.form';
import { CalculateSubCoolForm } from './forms/calculate-sub-cool.form';
import { CalculateSuperHeatForm } from './forms/calculate-super-heat.form';
import { TargetSuperHeatForm } from './forms/target-super-heat.form';

const getAnswerComponent = (message: IMessage) => {
    const selectedCalculator = (message.data as any).type;
    const calculationResult = (message.data as any).result;
    const inputParams = (message.data as any).inputParams;
    switch (selectedCalculator) {
        case CalculatorType.AirflowDiagnostic:
            return <AirflowDiagnosticsAnswer data={calculationResult} />;
        case CalculatorType.SuperHeat:
            return <CalculateSuperHeatAnswer data={calculationResult} />;
        case CalculatorType.SubCool:
            return <CalculateSubCoolAnswer data={calculationResult} />;
        case CalculatorType.DuctSizingCalculator:
            return <CalculatorDuctSizingAnswer data={calculationResult} />;
        case CalculatorType.NitrogenPressureTest:
            return <NitrogenPressureTestAnswer inputData={inputParams} data={calculationResult} />;
        case CalculatorType.PressureSetPoints:
            return <PressureSetPointsAnswer data={calculationResult} />;
        case CalculatorType.TXVSizeCalculator:
            return <TxvSizeCalculatorAnswer data={calculationResult} />;
        case CalculatorType.TargetSuperHeat:
            return <TargetSuperHeatAnswer data={calculationResult} />;
        default:
            return null;
    }
};

const getCalculatorForm = (message: IMessage): ReactNode => {
    const type = message.data as CalculatorType;
    switch (type) {
        case CalculatorType.AirflowDiagnostic:
            return <AirflowDiagnosticsForm />;
        case CalculatorType.DuctSizingCalculator:
            return <CalculatorDuctSizingForm />;
        case CalculatorType.NitrogenPressureTest:
            return <NitrogenPressureTestForm />;
        case CalculatorType.PressureSetPoints:
            return <PressureSetPointsForm />;
        case CalculatorType.TXVSizeCalculator:
            return <TxvSizeCalculatorForm />;
        case CalculatorType.SubCool:
            return <CalculateSubCoolForm />;
        case CalculatorType.SuperHeat:
            return <CalculateSuperHeatForm />;
        case CalculatorType.TargetSuperHeat:
            return <TargetSuperHeatForm />;
    }
    return null;
};

export const CalculatorPage: FC = provide({ singletons: [CalculatorStore, CalculatorApi] })(
    observer(() => {
        const [calculatorStore, chatStore] = useDependencies<[CalculatorStore, ChatStore]>(
            CalculatorStore,
            ChatStore
        );

        const { calculators, selectedCalculator, setSelectedCalculator } = calculatorStore;

        const renderMessage = useCallback(
            (message: IMessage, key: string) => {
                switch (message.type) {
                    case MessageType.CalculationResult:
                        return (
                            <MessageRow
                                isMine={message.isMine}
                                date={message.date}
                                showAvatar={message.showAvatar}
                            >
                                {selectedCalculator && getAnswerComponent(message)}
                            </MessageRow>
                        );
                    case MessageType.CalculatorForm:
                        return (
                            <MessageRow
                                isMine={message.isMine}
                                date={message.date}
                                showAvatar={message.showAvatar}
                            >
                                <MessageBubble isMine={message.isMine}>
                                    {selectedCalculator && getCalculatorForm(message)}
                                </MessageBubble>
                            </MessageRow>
                        );

                    case MessageType.Text:
                        return (
                            <MessageRow
                                key={key}
                                isMine={message.isMine}
                                showAvatar={message.showAvatar}
                                name={message.isMine ? 'Tenant Name' : 'TI'}
                                date={message.date}
                                data-cy={`message-row-${key}`}
                            >
                                <MessageBubble isMine={message.isMine}>
                                    <Text>{message.text}</Text>
                                </MessageBubble>
                            </MessageRow>
                        );
                    default:
                        return null;
                }
            },
            [selectedCalculator]
        );

        const handleNavigationButtonClick = () => {
            calculatorStore.dispose();
            chatStore.resetMessaging();
            window.history.back();
        };

        return (
            <ChatLayout
                subHeader={<Breadcrumbs />}
                header={
                    <ChatHeaderNew
                        onNavigationButtonClick={handleNavigationButtonClick}
                        icon="back"
                    />
                }
            >
                <MessageRow isMine={false} showAvatar>
                    <MessageBubble isMine={false} showBubble>
                        <Text>Please select from one of the options below:</Text>
                    </MessageBubble>
                </MessageRow>

                <MessageRow isMine={false} date={new Date()} showAvatar={false}>
                    <Flex className="w-100" wrap="wrap" gap="1">
                        {calculators.map(x => (
                            <ButtonToggle
                                disabled={x.disabled === true}
                                key={x.type}
                                className="m-t-1"
                                size="small"
                                checked={selectedCalculator === x.type}
                                onClick={() => setSelectedCalculator(x.type)}
                            >
                                {x.displayName}
                            </ButtonToggle>
                        ))}
                    </Flex>
                </MessageRow>
                {chatStore.messages.map((message, index) => {
                    return renderMessage(message, index.toString());
                })}
                {calculatorStore.isLoading && (
                    <MessageRow isMine={false}>
                        <Loader />
                    </MessageRow>
                )}
            </ChatLayout>
        );
    })
);
